<template>
  <view-wrapper name="Orders" :loading="loading" :loadingText="loadingText">
    <router-link class="button big primary white mt-3" to="addorder"
      >Create new order
    </router-link>
    <div class="justify-end flex items-center">
      <span class="mr-2">Show Archived</span>
      <Checkbox :value="includeArchived" @input="onToggleShowArchived" />
    </div>
    <modal
      v-if="showWarning"
      @close="showWarning = false"
      type="warning"
      heading="Warning"
      :description="warningText"
    />
    <expandabel-table
      class="mt-8"
      :columnsToNotSort="columnsToNotSort"
      :data="orders"
      :headers="headers"
      @searchQuery="onSearch"
    >
      <template v-slot:child="{ child }">
        <div class="order-button-container">
          <grafokett-button
            class="small"
            @click="$router.push(`orders/${child.parentId}`)"
            >Edit <i class="edit-symbol"
          /></grafokett-button>
          <grafokett-button class="small" @click="deleteOrder(child)"
            >Delete
          </grafokett-button>
        </div>
        <div class="flex">
          <p class="flex flex-auto">Articles included</p>
          <div class="flex">
            <p>Archive:</p>
            <Toggle
              class="flex ml-1 self-center"
              @click="toggleArchived(child)"
              :checked="child.archived"
            />
          </div>
        </div>

        <div class="filter-and-sort-box test">
          <div class="table">
            <table class="filter-table">
              <thead>
                <tr>
                  <th>Article name</th>
                  <th>Maximum print quantity</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(article, key) in child.articles"
                  :key="`article--${key}`"
                >
                  <td>{{ article.name }}</td>
                  <td>{{ article.maxQuantity }} left</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <p>Assigned to groups</p>
        <div class="filter-and-sort-box test">
          <div class="table">
            <table class="filter-table">
              <tbody>
                <tr v-for="(group, key) in child.groups" :key="`group--${key}`">
                  <td>{{ group.groupName }}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </expandabel-table>
    <div class="py-2">
      <div class="float-right">
        <i
          class="arrow left p-1 mx-1 mt-1 cursor-pointer"
          @click="setPage(page - 1)"
        />
        <span class="select-none">Page {{ page }}</span>
        <i
          class="arrow right p-1 mx-1 mt-1 cursor-pointer"
          @click="setPage(page + 1)"
        />
      </div>
    </div>
  </view-wrapper>
</template>

<script>
import { BehaviorSubject, combineLatest } from "rxjs";
import { debounceTime, distinctUntilChanged, mapTo } from "rxjs/operators";

import GrafokettButton from "../components/forms/Button.vue";
import ExpandabelTable from "../components/ExpandabelTable";
import Checkbox from "../components/forms/Checkbox.vue";
import Toggle from "components/forms/Toggle";
import Modal from "../components/Modal";

export default {
  components: {
    Toggle,
    GrafokettButton,
    ExpandabelTable,
    Checkbox,
    Modal,
  },
  data() {
    return {
      toggleActive: false,
      loading: false,
      showWarning: false,
      warningText: "",
      loadingText: "List of orders",
      page: 1,
      pageSize: 10,
      searchQuery: null,
      includeArchived: false,
      pageNumber$: null,
      pageSize$: null,
      searchQuery$: null,
      deletedOrder$: null,
      orders: [],
      headers: {
        name: "Order name",
        status: "Order status",
        archived: "archived",
      },
      columnsToNotSort: ["arrow"],
      importResult: {
        show: false,
        type: "success",
        heading: "Success",
        description:
          "Your order (s) have been queued for import and will shortly be available",
      },
    };
  },
  methods: {
    async loadOrders(searchQuery, includeArchived, page, pageSize) {
      this.loading = true;
      const response = await this.$store.$api.get(
        `/api/orders?take=${pageSize}&offset=${pageSize *
          (page -
            1)}&searchQuery=${searchQuery}&includeArchived=${includeArchived}`
      );
      this.orders = response.data.data.map(o => ({
        id: o.id,
        name: o.orderReference,
        status: o.status,
        archived: o.archived,
        expandableData: { ...o },
      }));
      this.loading = false;
    },
    async deleteOrder(order) {
      this.loading = true;
      const { data } = await this.$store.$api.get(
        `/api/orders/${order.parentId}/usage`
      );
      if (!data.data.length > 0) {
        await this.$store.$api.delete(`/api/orders/${order.parentId}`);
        this.deletedOrder$.next(order.parentId);
      } else {
        this.loading = false;
        this.warningText =
          "This order can't be removed because it has been printed.";
        this.showWarning = true;
      }
    },
    async toggleArchived(order) {
      this.loading = true;
      await this.$store.$api.put(`/api/orders/archive/`, {
        orderId: order.parentId,
      });
      this.loading = false;
    },
    setPage(newPage) {
      this.pageNumber$.next(Math.max(1, newPage));
    },
    onSearch(value) {
      this.searchQuery$.next(value);
    },
    onToggleShowArchived(value) {
      this.includeArchived = !this.includeArchived;
      this.includeArchived$.next(value);
    },
  },
  mounted() {
    this.pageNumber$ = new BehaviorSubject(1);
    this.pageNumber$$ = this.pageNumber$.subscribe(
      value => (this.page = value)
    );

    this.searchQuery$ = new BehaviorSubject("");
    this.includeArchived$ = new BehaviorSubject(false);
    this.searchQuery$$ = this.searchQuery$
      .pipe(
        distinctUntilChanged(),
        mapTo(1)
      )
      .subscribe(value => {
        this.pageNumber$.next(value);
      });
    this.pageSize$ = new BehaviorSubject(10);
    this.deletedOrder$ = new BehaviorSubject(0);

    this.searchStream$$ = combineLatest([
      this.searchQuery$.pipe(distinctUntilChanged()),
      this.includeArchived$.pipe(),
      this.pageNumber$.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ),
      this.pageSize$,
      this.deletedOrder$,
    ])
      .pipe(debounceTime(300))
      .subscribe(([searchQuery, includeArchived, pageNumber, pageSize]) => {
        this.loadOrders(
          encodeURIComponent(searchQuery || ""),
          includeArchived,
          pageNumber,
          pageSize
        );
      });
  },
  beforeDestroy() {
    this.pageNumber$$.unsubscribe();
    this.searchQuery$$.unsubscribe();
    this.searchStream$$.unsubscribe();
  },
};
</script>
